/**
 * The Upload page's graphic: a bundle being signed, pushed, and picked up by
 * peers.
 *
 * The page used to carry a five-step walkthrough under the form, teaching
 * `mero-sign` and `calimero-registry bundle create` — a flow the docs page
 * explicitly says is replaced by `cargo mero`. Two sets of instructions, one
 * of them wrong, is worse than one; the steps are gone and this stands where
 * they were, with the reading pointed at Docs.
 *
 * ⚠️ CSS/SMIL ONLY — NO JAVASCRIPT LOOP. Same rule as the hero: a rAF loop on
 * a page runs forever in every background tab, and it bypasses
 * `prefers-reduced-motion`, which is honoured globally here through a media
 * query that can only reach declarative animation. Everything below animates
 * on the compositor and stops when the tab is hidden.
 *
 * ⚠️ The palette is the poster gallery's, not the page's: calimero.network's
 * charcoal panel with lime line-art and near-white type. It does not swap
 * with the theme — on paper it reads as one of the landing's dark bands.
 */

const INK = '#fcfcfc';
const MARK = '#a5ff11';

export function PublishArt() {
  return (
    <div
      data-testid='publish-art'
      className='relative overflow-hidden border border-line'
      style={{
        background:
          'linear-gradient(112deg, #1f1e22 0%, #19181b 46%, #141316 100%)',
      }}
    >
      {/*
        ⚠️ THE CAPTION IS ONLY AN OVERLAY WHERE THERE IS ROOM FOR ONE. At
        21:9 a phone gives this box about 147px of height, and the two lines
        sitting `absolute bottom-0` inside it came to ~110 of them — so the
        headline ran straight through the registry card and the peers, and the
        drawing read as a smudge behind the type.

        Under `sm` the caption is in normal flow BELOW the art, which is also
        what makes the block taller: the picture keeps its full 21:9 and the
        words get their own space rather than borrowing it. From `sm` up the
        old overlay is restored — the composition leaves its lower left empty
        on purpose, and there the text has somewhere to sit.
      */}
      <div className='relative aspect-[21/9] w-full'>
        <svg
          viewBox='0 0 840 360'
          className='absolute inset-0 h-full w-full'
          role='img'
          aria-label='A signed bundle being published and picked up by peers'
        >
          <defs>
            <linearGradient id='pa-card' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#2c2a30' stopOpacity='1' />
              <stop offset='100%' stopColor='#222025' stopOpacity='1' />
            </linearGradient>
          </defs>

          {/* Quiet shapes, so the ground is composed rather than blank. */}
          <circle cx='90' cy='300' r='120' fill={MARK} opacity='0.06' />
          <circle cx='700' cy='40' r='90' fill={MARK} opacity='0.05' />

          {/* ── The bundle ── */}
          <g className='pa-bundle'>
            <rect
              x='96'
              y='128'
              width='132'
              height='104'
              fill='url(#pa-card)'
              stroke={MARK}
              strokeOpacity='0.35'
            />
            <rect
              x='116'
              y='152'
              width='64'
              height='8'
              fill={INK}
              opacity='0.5'
            />
            <rect
              x='116'
              y='170'
              width='92'
              height='6'
              fill={INK}
              opacity='0.22'
            />
            <rect
              x='116'
              y='184'
              width='74'
              height='6'
              fill={INK}
              opacity='0.18'
            />
            {/* the seal, drawn on as the bundle is signed */}
            <circle
              className='pa-seal'
              cx='202'
              cy='208'
              r='14'
              fill={MARK}
              opacity='0.9'
            />
            <path
              className='pa-tick'
              d='M195 208l5 5 9-10'
              fill='none'
              stroke='#131215'
              strokeWidth='2.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </g>

          {/* ── The wire ── */}
          <path
            id='pa-wire'
            d='M232 180 H600'
            fill='none'
            stroke={MARK}
            strokeOpacity='0.25'
            strokeWidth='2'
            strokeDasharray='6 8'
          />
          {/* the packet, travelling it */}
          <circle className='pa-packet' r='7' fill={MARK}>
            <animateMotion
              dur='4.6s'
              repeatCount='indefinite'
              keyPoints='0;0;1;1'
              keyTimes='0;0.28;0.78;1'
              calcMode='linear'
            >
              <mpath href='#pa-wire' />
            </animateMotion>
          </circle>

          {/* ── The registry ── */}
          <g>
            <rect
              x='600'
              y='120'
              width='150'
              height='120'
              fill='url(#pa-card)'
              stroke={MARK}
              strokeOpacity='0.35'
            />
            {[0, 1, 2].map(i => (
              <rect
                key={i}
                className={`pa-row pa-row-${i}`}
                x='620'
                y={146 + i * 28}
                width='110'
                height='18'
                fill={MARK}
                opacity='0.14'
              />
            ))}
          </g>

          {/* ── The peers ── */}
          {[
            [676, 300],
            [744, 276],
            [608, 276],
          ].map(([cx, cy], i) => (
            <g key={i}>
              <line
                x1='675'
                y1='240'
                x2={cx}
                y2={cy}
                stroke={MARK}
                strokeOpacity='0.2'
                strokeWidth='1.5'
              />
              <circle
                className={`pa-peer pa-peer-${i}`}
                cx={cx}
                cy={cy}
                r='13'
                fill='#1f1e22'
                stroke={MARK}
                strokeOpacity='0.45'
              />
            </g>
          ))}
        </svg>
      </div>

      <div className='relative px-5 pb-5 pt-4 sm:absolute sm:inset-x-0 sm:bottom-0 sm:p-7'>
        <p
          className='text-[16px] font-bold uppercase leading-tight tracking-[0.12em] sm:text-[18px]'
          style={{ color: INK }}
        >
          Sign it, push it, and every node can verify it.
        </p>
        <p
          className='mt-1 text-[14px] font-light leading-snug sm:text-[15px]'
          style={{ color: INK, opacity: 0.7 }}
        >
          A bundle is WASM plus a signed manifest. The registry checks the
          signature; peers check it again on install.
        </p>
      </div>
    </div>
  );
}
